import React from "react";
import { Bookmark, BookmarkCheck, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";

function Block({ block }) {
  if (block.type === "p") return <p className="text-base sm:text-lg leading-relaxed">{block.text}</p>;
  if (block.type === "h")
    return (
      <h3 className="serif text-2xl sm:text-3xl mt-8 mb-3 text-primary">{block.text}</h3>
    );
  if (block.type === "ul")
    return (
      <ul className="list-disc pl-6 space-y-2">
        {block.items.map((it, i) => (
          <li key={i} className="text-base leading-relaxed">
            <span dangerouslySetInnerHTML={{ __html: it }} />
          </li>
        ))}
      </ul>
    );
  if (block.type === "ol")
    return (
      <ol className="list-decimal pl-6 space-y-2">
        {block.items.map((it, i) => (
          <li key={i} className="text-base leading-relaxed">
            <span dangerouslySetInnerHTML={{ __html: it }} />
          </li>
        ))}
      </ol>
    );
  if (block.type === "img")
    return (
      <figure className="my-6 rounded-md overflow-hidden border border-border bg-secondary">
        <img src={block.src} alt={block.alt || block.caption || ""} className="w-full h-auto" />
        {block.caption && (
          <figcaption className="mono text-xs text-muted-foreground px-4 py-2 border-t border-border">
            {block.caption}
          </figcaption>
        )}
      </figure>
    );
  if (block.type === "callout") {
    const variants = {
      info: "border-primary/40 bg-primary/5",
      tip: "border-accent/40 bg-accent/10",
      warn: "border-destructive/40 bg-destructive/5",
      example: "border-foreground/30 bg-secondary",
    };
    const cls = variants[block.variant] || variants.info;
    return (
      <div className={`my-5 rounded-md border-l-4 p-4 ${cls}`}>
        {block.title && (
          <div className="mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
            {block.title}
          </div>
        )}
        <div className="text-base leading-relaxed">{block.text}</div>
      </div>
    );
  }
  if (block.type === "table")
    return (
      <div className="my-5 overflow-x-auto border border-border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr>
              {block.headers.map((h, i) => (
                <th key={i} className="text-left font-semibold px-4 py-2 border-b border-border">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((r, i) => (
              <tr key={i} className="odd:bg-background even:bg-background/50">
                {r.map((c, j) => (
                  <td
                    key={j}
                    className="px-4 py-2 border-b border-border align-top"
                    dangerouslySetInnerHTML={{ __html: c }}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  return null;
}

export default function SubTopicView({
  moduleNumber,
  sub,
  bookmarked,
  completed,
  onToggleBookmark,
  onMarkDone,
}) {
  return (
    <article className="max-w-3xl" id={sub.id} data-testid={`subtopic-${sub.id}`}>
      <div className="flex items-center gap-3 mb-2">
        <span className="mono text-[11px] uppercase tracking-widest text-muted-foreground">
          Module {moduleNumber} • Sub-topic
        </span>
        <span className="inline-flex items-center gap-1 mono text-[11px] text-muted-foreground">
          <Clock className="w-3.5 h-3.5" />
          {sub.readingTime}
        </span>
      </div>

      <div className="flex items-start justify-between gap-4 mb-4">
        <h2 className="serif text-3xl sm:text-4xl leading-tight text-foreground">{sub.title}</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleBookmark}
          aria-label="toggle bookmark"
          data-testid={`bookmark-toggle-${sub.id}`}
        >
          {bookmarked ? (
            <BookmarkCheck className="w-5 h-5 text-accent fill-accent" />
          ) : (
            <Bookmark className="w-5 h-5" />
          )}
        </Button>
      </div>

      <div className="prose-reader space-y-4">
        {sub.blocks.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </div>

      <div className="mt-8 pt-6 border-t border-border flex items-center justify-between">
        {completed ? (
          <span className="inline-flex items-center gap-2 text-sm text-primary">
            <CheckCircle2 className="w-4 h-4" /> Marked as read
          </span>
        ) : (
          <Button onClick={onMarkDone} data-testid={`mark-done-${sub.id}`}>
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Mark as read
          </Button>
        )}
      </div>
    </article>
  );
}
